/**
 * RL-997 home readings for the care team (`/chronic/reading-alerts`,
 * `/chronic/home-readings/{patientId}`, `/chronic/reading-thresholds/{patientId}`).
 * Shapes follow ommri-care-api `home-readings.examples.ts` and
 * `home-reading-rules.ts`.
 */
export const READING_KINDS = ['blood_pressure', 'glucose'] as const;
export type ReadingKind = (typeof READING_KINDS)[number];

export const ALERT_STATUS_FILTERS = [
  'active',
  'open',
  'acknowledged',
  'resolved',
] as const;
export type AlertStatusFilter = (typeof ALERT_STATUS_FILTERS)[number];
export type AlertStatus = Exclude<AlertStatusFilter, 'active'>;

export type OutOfRange = 'bp_high' | 'glucose_low' | 'glucose_high';
export type ReadingSource =
  'manual' | 'apple_health' | 'health_connect' | 'bluetooth';
export type GlucoseContext = 'fasting' | 'random' | 'after_meal';

export interface StaffRef {
  id: string;
  fullName: string | null;
}

export interface HomeReading {
  id: string;
  clientId: string;
  kind: ReadingKind;
  systolic: number | null;
  diastolic: number | null;
  pulse: number | null;
  glucoseMmol: number | null;
  glucoseContext: GlucoseContext | null;
  measuredAt: string;
  source: ReadingSource;
  deviceName: string | null;
  outOfRange: OutOfRange | null;
  createdAt: string;
}

export interface ReadingAlert {
  id: string;
  patient: { id: string; fullName: string };
  branch: { id: string; name: string };
  kind: ReadingKind;
  status: AlertStatus;
  readingCount: number;
  latestReading: HomeReading | null;
  lastReadingAt: string;
  createdAt: string;
  /** The doctor the alert was routed to; null = the branch care team. */
  doctor: StaffRef | null;
  acknowledgedAt: string | null;
  acknowledgedBy: StaffRef | null;
  resolvedAt: string | null;
  resolvedBy: StaffRef | null;
  note: string | null;
  version: number;
}

export interface ReadingAlertList {
  alerts: ReadingAlert[];
  hasMore: boolean;
}

export interface Thresholds {
  bpSystolicHigh: number;
  bpDiastolicHigh: number;
  glucoseLow: number;
  glucoseHigh: number;
}
export type ThresholdKey = keyof Thresholds;
export type OwnThresholds = { [K in ThresholdKey]: number | null };

export interface ReadingsPage {
  patient: { id: string; fullName: string };
  kind: ReadingKind;
  readings: (HomeReading & { alertId: string | null })[];
  hasMore: boolean;
  nextCursor: string | null;
  stats: {
    days: number;
    count: number;
    average: {
      value: number | null;
      systolic: number | null;
      diastolic: number | null;
    };
    lowest: number | null;
    highest: number | null;
    outOfRangeCount: number;
    trend: 'up' | 'down' | 'steady' | null;
  };
  alertThresholds: Thresholds & { setByCareTeam: boolean };
  chartBand: { low: number; high: number };
}

export interface ThresholdsView {
  patient: { id: string; fullName: string };
  own: OwnThresholds;
  defaults: Thresholds;
  effective: Thresholds;
  /** 0 until first saved. */
  version: number;
  updatedAt: string | null;
}
